import type { WithActiveBranchId } from '@app/api/shared';

export enum SubscriptionsEndpoints {
  GetSubscriptions = 'GetSubscriptions',
  GetSubscriptionById = 'GetSubscriptionById',
  CreateSubscription = 'CreateSubscription',
  EditSubscription = 'EditSubscription',
  DeleteSubscription = 'DeleteSubscription',
  GetSubscriptionsForSale = 'GetSubscriptionsForSale',
  GetRecordSubscriptions = 'GetRecordSubscriptions',
  SetRecordSubscriptions = 'SetRecordSubscriptions',
  GetClientSubscriptions = 'GetClientSubscriptions',
}

/** Услуга на абонементе */
export interface ModelSubscriptionService {
  Id: number;
  /** ID услуги */
  ClientServiceId: number;
  Name: string | null;
  /** Кол-во услуги на абонементе */
  Count: number;
  /** Стоимость всего кол-ва услуги без учёта скидки */
  Cost: number;
  /** Общая скидка со всего кол-ва услуги на абонементе */
  Discount: number;
}

/** Абонемент */
export interface ModelSubscription {
  Id: number;
  BranchId: number;
  Name: string | null;
  /** Срок действия в днях с момента покупки, null — бессрочный */
  ValidFromPurchaseDays: number | null;
  /** Стоимость абонемента с учётом всех услуг и их скидок */
  SubscriptionCost: number;
  Services: ModelSubscriptionService[] | null;
}

/** Абонемент на записи: строка продажи в окне записи */
export interface ModelRecordSubscription {
  /** ID проданного абонемента клиента, 0 — абонемент ещё не продан */
  Id: number;
  /** ID абонемента из реестра */
  SubscriptionId: number;
  Name: string | null;
  /** Продавец при допродаже, null — без допродажи */
  SellerId: number | null;
  /**
   * Стоимость абонемента: в списке для продажи — без скидки записи,
   * у проданного — уже за вычетом скидки (Discount)
   */
  Cost: number;
  /** Скидка в рублях, задаётся в окне записи */
  Discount: number;
  /** Кол-во — всегда 1 */
  Count: number;
  /** Абонемент не оплачен — его можно изменить и удалить с записи */
  CanEditAndDelete: boolean;
}

/** Абонементы записи */
export interface ModelRecordSubscriptions {
  RecordId: number;
  Subscriptions: ModelRecordSubscription[] | null;
}

/** Скидка на услугу (ModelRecordServiceDiscount) */
export interface ModelClientSubscriptionServiceDiscount {
  /** Сумма скидки */
  Discount: number;
  /** Тип скидки */
  Type: string;
}

/**
 * Свободная единица услуги на абонементе клиента (ModelClientServiceLite).
 * Бэкенд отдаёт каждую единицу отдельной строкой с Count = 1 и ценой 0.
 */
export interface ModelClientSubscriptionService {
  /** ID услуги из справочника услуг */
  Id: number;
  Name: string | null;
  CategoryId: number;
  Category: string | null;
  PriceS: number;
  PriceE: number | null;
  /** Продолжительность в минутах */
  Duration: number;
  Count: number;
  UserId: number | null;
  /** Скидка в рублях */
  Discount: number;
  Paid: number;
  /** Услуга в архиве */
  IsDeleted: boolean;
  /** ID абонемента клиента, с которого берётся услуга */
  ClientSubscriptionId: number | null;
  Discounts: ModelClientSubscriptionServiceDiscount[];
  IsExcludedFromLoyaltyDiscount: boolean;
}

/**
 * Оплаченный абонемент клиента со свободными услугами (GetClientSubscriptions).
 * Абонемент без свободных услуг бэкенд не отдаёт.
 */
export interface ModelClientSubscription {
  ClientId: number;
  SubscriptionName: string | null;
  /** ID абонемента клиента */
  ClientSubscriptionId: number;
  SubscriptionServices: ModelClientSubscriptionService[] | null;
}

export type GetSubscriptionsRequest = WithActiveBranchId;

export type GetSubscriptionByIdRequest = WithActiveBranchId<{
  subscriptionId: number;
}>;

export type CreateSubscriptionRequest = WithActiveBranchId<{
  data: ModelSubscription;
}>;

export type EditSubscriptionRequest = WithActiveBranchId<{
  data: ModelSubscription;
}>;

export type DeleteSubscriptionRequest = WithActiveBranchId<{
  subscriptionId: number;
}>;

export type GetSubscriptionsForSaleRequest = WithActiveBranchId;

export type GetRecordSubscriptionsRequest = WithActiveBranchId<{
  recordId: number;
}>;

export type GetClientSubscriptionsRequest = WithActiveBranchId<{
  clientId: number;
}>;

/** Строки с Id = 0 продаются клиенту записи */
export type SetRecordSubscriptionsRequest = WithActiveBranchId<{
  data: ModelRecordSubscriptions;
}>;

export type GetSubscriptionsResponse = ModelSubscription[];
export type GetSubscriptionByIdResponse = ModelSubscription;
export type CreateSubscriptionResponse = ModelSubscription;
export type EditSubscriptionResponse = ModelSubscription;
export type DeleteSubscriptionResponse = void;
export type GetSubscriptionsForSaleResponse = ModelRecordSubscription[];
export type GetRecordSubscriptionsResponse = ModelRecordSubscriptions;
export type SetRecordSubscriptionsResponse = ModelRecordSubscriptions;
export type GetClientSubscriptionsResponse = ModelClientSubscription[];
